import type {
    EFFICIENCY_CLASS,
    EFFICIENCY_CLASS_EMPTY_BATTERY_TMEF,
    EFFICIENCY_CLASS_LEXUS,
    EFFICIENCY_CLASS_TMEF,
} from "../../../../common-deprecated/constants";
import type { SpecDataType } from "../../../../common-deprecated/types/SpecTypes";
import type { EcoConsumptionLabelType } from "../utils/labels";

export enum EcoConsumptionSectionEnum {
    Electric = "electric",
    Petrol = "petrol",
    Diesel = "diesel",
    Hybrid = "hybrid",
    Hydrogen = "hydrogen",
    PlugIn = "plugin",
}

export enum EcoConsumptionAreaEnum {
    FuelArea = "fuel",
    SpecArea = "spec",
    EmissionArea = "emission",
    CostsArea = "costs",
    DisclaimerArea = "disclaimer",
}

type EcoConsumptionGenericAreaType = {
    title?: string;
    subtitle?: string;
};

export type EcoConsumptionFuelAreaType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.FuelArea;
    primary: { label: string; data?: SpecDataType | string };
    secondary: { label: string; data?: SpecDataType | string };
};

export type EcoConsumptionSpecType = {
    label: string;
    data: (SpecDataType | null)[];
    disclaimer?: SanitizedString; // Disclaimer
    highlighted: boolean; // So we can render the spec bold
};

export type EcoConsumptionSpecAreaType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.SpecArea;
    specifications: EcoConsumptionSpecType[];
};

export type EcoConsumptionEmissionAreaType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.EmissionArea;
    emissionEntries: {
        label: string;
        value?: string;
    }[];
};

export type EcoConsumptionCostEntryType = {
    title: string;
    subtitle?: string;
    disclaimer?: SanitizedString;
    highlighted: boolean;
    price?: string | number | null;
    taxExempt?: string;
};

export type EcoConsumptionCostsAreaType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.CostsArea;
    yearlyMilageEnergyCosts?: EcoConsumptionCostEntryType;
    co2Costs: EcoConsumptionCostEntryType[];
    total?: EcoConsumptionCostEntryType;
};

export type EcoConsumptionDisclaimerAreaType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.DisclaimerArea;
    generalDisclaimer: SanitizedString;
    disclaimers: SanitizedString[];
};

export type EcoConsumptionAreaType =
    | EcoConsumptionFuelAreaType
    | EcoConsumptionSpecAreaType
    | EcoConsumptionEmissionAreaType
    | EcoConsumptionCostsAreaType
    | EcoConsumptionDisclaimerAreaType;

export type EcoConsumptionSectionType = {
    title: string;
    type: EcoConsumptionSectionEnum;
    areas: EcoConsumptionAreaType[];
};

export type EcoConsumptionFuelAreaAceType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.FuelArea;
    primaryLabel: string;
    primaryValue?: string;
    primaryValueFallback?: string;
    secondaryLabel: string;
    secondaryValue?: string;
    secondaryValueFallback?: string;
};

type EcoConsumptionSpecAceType = {
    key: string;
    fallbacks: string[];
};

export type EcoConsumptionSpecificationAceType = {
    label: string;
    data: EcoConsumptionSpecAceType[];
    disclaimer: SanitizedString;
    highlighted: boolean;
};

export type EcoConsumptionSpecAreaAceType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.SpecArea;
    specifications?: EcoConsumptionSpecificationAceType[];
};
export type EcoConsumptionEmissionAreaAceType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.EmissionArea;
    emissionEntries?: {
        label: string;
        value:
            | typeof EFFICIENCY_CLASS
            | typeof EFFICIENCY_CLASS_LEXUS
            | typeof EFFICIENCY_CLASS_TMEF
            | typeof EFFICIENCY_CLASS_EMPTY_BATTERY_TMEF;
    }[];
};
export type EcoConsumptionCostsAreaAceType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.CostsArea;
    yearlyMilageEnergyCostsTitle?: string;
    yearlyMilageEnergyCostsSubTitle?: string;
    co2CostsLongTermTitle?: string;
    co2CostsLongTermDisclaimer?: SanitizedString;
    co2CostsByMedianPriceTitle?: string;
    co2CostsByLowPriceTitle?: string;
    co2CostsByHighPriceTitle: string;
    totalLabel: string;
    totalPriceOverride: string;
    totalDisclaimer: SanitizedString;
};
export type EcoConsumptionDisclaimerAreaAceType = EcoConsumptionGenericAreaType & {
    type: EcoConsumptionAreaEnum.DisclaimerArea;
    generalDisclaimer?: SanitizedString;
};

export type EcoConsumptionAreaAceType =
    | EcoConsumptionFuelAreaAceType
    | EcoConsumptionSpecAreaAceType
    | EcoConsumptionEmissionAreaAceType
    | EcoConsumptionCostsAreaAceType
    | EcoConsumptionDisclaimerAreaAceType;

export type EcoConsumptionSectionConfigEntryAceType = {
    title: string;
    type?: EcoConsumptionSectionEnum;
    areas?: EcoConsumptionAreaAceType[];
};

export type EcoConsumptionSectionConfigAceType = {
    default: EcoConsumptionSectionConfigEntryAceType;
    models: ({ model: string } & EcoConsumptionSectionConfigEntryAceType)[];
};

export type EcoConsumptionAceType = {
    ecoConsumptionCollection: {
        petrol: EcoConsumptionSectionConfigAceType;
        diesel: EcoConsumptionSectionConfigAceType;
        hybrid: EcoConsumptionSectionConfigAceType;
        plugin: EcoConsumptionSectionConfigAceType;
        electric: EcoConsumptionSectionConfigAceType;
        hydrogen: EcoConsumptionSectionConfigAceType;
    };
    ecoConsumptionLabels: Record<EcoConsumptionLabelType, string>;
    error?: string;
};

export type EcoConsumptionCostType = {
    co2CostLow: number; // low CO2 emission cost in euros
    co2CostMedium?: number; // average CO2 emission cost in euros
    co2CostHigh: number; // high CO2 emission cost in euros
    co2TonPriceLow: number; // low CO2 ton price in euros
    co2TonPriceMedium?: number; // average CO2 ton price in euros
    co2TonPriceHigh: number; // high CO2 ton price in euros
    electricityKwhPriceCents?: number; // electricity kWh price in cents
    fuelUnitPriceEuros?: number; // fuel unit (litre or kg) price in euros
    taxYearly?: number; // total yearly tax amount in euros
    yearlyCostEuros: number; // yearly fuel and/or energy cost
    pricesForYear: string; // year for which fuel/energy prices are used
    disclaimerFrom: string; // disclaimer "from" validity value
    disclaimerTo: string; // disclaimer "to" validity value
    errors: string[]; // array of string for each error, each error usually corresponds with a NaN value.
};
